<template>
  <aside
    v-if="enabled"
    class="foil-debug-hud"
    aria-label="Foil debug"
  >
    <header class="foil-debug-hud__bar">
      <strong>foil debug</strong>
      <span class="foil-debug-hud__mode">{{ modeLabel }}</span>
    </header>
    <p class="foil-debug-hud__hint">
      Misma carta, mismo foil, tres URLs. Copia el informe de cada una.
    </p>
    <label class="foil-debug-hud__toggle">
      <input
        type="checkbox"
        :checked="blendOff"
        @change="onBlendToggle"
      >
      Apagar mix-blend (velo sólido)
    </label>
    <pre v-if="text" class="foil-debug-hud__pre">{{ text }}</pre>
    <p v-else class="foil-debug-hud__hint">
      Abre Colección y selecciona una carta foil (visor grande).
    </p>
    <div class="foil-debug-hud__actions">
      <button type="button" class="foil-debug-hud__btn" @click="copy">
        Copiar informe
      </button>
      <span v-if="copied" class="foil-debug-hud__ok">copiado</span>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  isFoilBlendOff,
  isFoilDebugEnabled,
  setFoilBlendOff,
  snapshotCardArt,
  subscribeFoilDebug,
  type FoilDebugReport,
} from '../utils/foilDebug'

const enabled = ref(false)
const blendOff = ref(false)
const report = ref<FoilDebugReport | null>(null)
const copied = ref(false)
let stop: (() => void) | null = null
let copyTimer = 0

const modeLabel = computed(() => {
  const r = report.value
  if (!r) return ''
  return r.prod ? `prod/${r.mode}` : `dev/${r.mode}`
})

const text = computed(() => {
  const r = report.value
  if (!r) return ''
  const wash = r.layers.wash
  const shine = r.layers.shine
  const lines = [
    `origin          ${r.origin}`,
    `mode            ${r.mode}  prod=${r.prod}`,
    `card            ${r.alt || '(sin alt)'}`,
    `foil prop       ${r.foilProp}`,
    `foil resolved   ${r.foilResolved}`,
    `classes         ${r.rootClass}`,
    `img protocol    ${r.imgProtocol}`,
    `img src         ${r.imgSrc}`,
    `img size        ${r.imgNatural} complete=${r.imgComplete}`,
    `plus-lighter    supports=${r.supportsPlusLighter}`,
    `autoDark        ${r.autoDark}  darkReader=${r.darkReader}`,
    `html scheme     ${r.htmlColorScheme}  meta=${r.metaColorScheme}`,
    `wash blend      ${wash?.mixBlendMode ?? '—'}  opacity=${wash?.opacity ?? '—'}`,
    `shine blend     ${shine?.mixBlendMode ?? '—'}`,
    `face isolation  ${r.face.isolation}  transform=${r.face.transform}`,
    `root box-shadow ${r.root.boxShadow}`,
    `style tags      ${r.styleTagCount}`,
    `css files       ${r.cssHrefs.join(' | ') || '(inline only)'}`,
    `killers`,
    ...r.killers.map((k) => `  - ${k}`),
  ]
  return lines.join('\n')
})

function onBlendToggle(ev: Event): void {
  const on = (ev.target as HTMLInputElement).checked
  setFoilBlendOff(on)
  blendOff.value = on
  const el = document.querySelector(
    '.card-art.is-foil.glow-full',
  ) as HTMLElement | null
  if (!el || !report.value) return
  report.value = snapshotCardArt(el, {
    foilProp: report.value.foilProp,
    foilResolved: report.value.foilResolved,
    foilReady: report.value.foilReady,
    alt: report.value.alt,
  })
}

async function copy(): Promise<void> {
  if (!report.value) return
  const payload = JSON.stringify(report.value, null, 2)
  await navigator.clipboard.writeText(payload)
  copied.value = true
  window.clearTimeout(copyTimer)
  copyTimer = window.setTimeout(() => {
    copied.value = false
  }, 1600)
}

onMounted(() => {
  enabled.value = isFoilDebugEnabled()
  blendOff.value = isFoilBlendOff()
  if (!enabled.value) return
  stop = subscribeFoilDebug((next) => {
    report.value = next
  })
})

onUnmounted(() => {
  stop?.()
  window.clearTimeout(copyTimer)
})
</script>

<style>
html[data-foil-blend="off"] .foil-wash,
html[data-foil-blend="off"] .foil-shine,
html[data-foil-blend="off"] .foil-fx,
html[data-foil-blend="off"] .foil-fx2 {
  mix-blend-mode: normal !important;
}
</style>

<style scoped>
.foil-debug-hud {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 99999;
  width: min(420px, calc(100vw - 24px));
  max-height: min(52vh, 520px);
  overflow: auto;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(12, 12, 16, 0.94);
  color: #e7e5e4;
  font: 11px/1.4 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
  pointer-events: auto;
}

.foil-debug-hud__bar {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  color: #fbbf24;
}

.foil-debug-hud__mode {
  color: #a8a29e;
}

.foil-debug-hud__hint {
  margin: 0 0 8px;
  color: #a8a29e;
}

.foil-debug-hud__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}

.foil-debug-hud__pre {
  margin: 0 0 8px;
  white-space: pre-wrap;
    word-break: break-all;
  color: #d6d3d1;
}

.foil-debug-hud__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.foil-debug-hud__btn {
  border: 0;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fbbf24;
  color: #1c1917;
  font: inherit;
  cursor: pointer;
}

.foil-debug-hud__ok {
  color: #86efac;
}
</style>
